import { useCallback } from 'react';
import {
    HStack,
    Tag,
    VStack,
    Button,
    IconButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@cad-arc';
import { SearchInvolvedEntity } from '@mark43/cad-api';
import { EventPanelTab } from '../event-panel/tabs/components/event-panel-tabs-config';
import { useOpenEventPanel } from '../event-panel/use-open-event-panel';
import { useOpenFrEventDetails } from '../fr-event-details-page/hooks/use-open-fr-event-details';
import { FR_EVENT_DETAILS_TABS } from '../fr-event-details-page/constants';
import { useApplicationType } from '../common/hooks/use-application-type';

export function EventSearchResultsInvolvedEntitiesCell({
    entities,
    eventId,
    eventPanelTab,
}: {
    entities: SearchInvolvedEntity[];
    eventId: number;
    eventPanelTab: EventPanelTab;
}) {
    const truncatedEntities = entities.slice(0, 2);
    const overflowEntities = entities.slice(2);
    return (
        <VStack align="left">
            {truncatedEntities[0] && (
                <InvolvedEntityTags
                    entities={[truncatedEntities[0]]}
                    eventId={eventId}
                    eventPanelTab={eventPanelTab}
                />
            )}
            <HStack>
                {truncatedEntities[1] && (
                    <InvolvedEntityTags
                        entities={[truncatedEntities[1]]}
                        eventId={eventId}
                        eventPanelTab={eventPanelTab}
                    />
                )}
                {overflowEntities.length > 0 && (
                    <OverflowEntitiesPopover
                        entities={overflowEntities}
                        eventId={eventId}
                        eventPanelTab={eventPanelTab}
                    />
                )}
            </HStack>
        </VStack>
    );
}

function InvolvedEntityTags({
    entities,
    eventId,
    eventPanelTab,
}: {
    entities: SearchInvolvedEntity[];
    eventId: number;
    eventPanelTab: EventPanelTab;
}) {
    const { isFirstResponder } = useApplicationType();
    const openEventPanel = useOpenEventPanel();
    const openFrEventDetails = useOpenFrEventDetails();
    const openEventDetailsHandler = useCallback(() => {
        if (isFirstResponder) {
            openFrEventDetails(eventId, FR_EVENT_DETAILS_TABS.EVENT_INFO);
            return;
        }

        openEventPanel({
            eventId,
            shouldFocusPanel: true,
            workspacePanelConfig: {
                panelState: {
                    initialTab: eventPanelTab,
                },
            },
        });
    }, [eventId, eventPanelTab, isFirstResponder, openEventPanel, openFrEventDetails]);

    return (
        <VStack align="left" width="fit-content">
            {entities.map((entity, index) => (
                <Tag
                    key={index}
                    trailingVisual={
                        <IconButton
                            variant="ghost"
                            icon="OpenNewWindow"
                            aria-label="open event"
                            size="sm"
                            colorVariant="white"
                            onClick={openEventDetailsHandler}
                        />
                    }
                >
                    {entity.name}
                </Tag>
            ))}
        </VStack>
    );
}

function OverflowEntitiesPopover({
    entities,
    eventId,
    eventPanelTab,
}: {
    entities: SearchInvolvedEntity[];
    eventId: number;
    eventPanelTab: EventPanelTab;
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="sm">{`+${entities.length}`}</Button>
            </PopoverTrigger>
            <PopoverContent>
                <InvolvedEntityTags
                    entities={entities}
                    eventId={eventId}
                    eventPanelTab={eventPanelTab}
                />
            </PopoverContent>
        </Popover>
    );
}
